import React, { useState } from 'react'
import { theme } from '../../styles/theme'
import { TrendingUp, MapPin, Users, ArrowRight } from 'lucide-react'
import { Card } from '../common/Card'
import { Badge } from '../common/Badge'
import { useNavigate } from 'react-router-dom'

export interface MarketOpportunity {
  country: string
  countryCode: string
  flag: string
  score: number
  marketSize: string
  growthRate: number
  competition: 'Low' | 'Medium' | 'High'
  sellerCount: number
}

interface MarketOpportunityItemProps {
  opportunity: MarketOpportunity
  onClick?: () => void
}

const competitionColors = {
  Low: 'success',
  Medium: 'warning',
  High: 'error',
} as const

export const MarketOpportunityItem: React.FC<MarketOpportunityItemProps> = ({
  opportunity,
  onClick,
}) => {
  const [isHovered, setIsHovered] = useState(false)

  const scoreColor = opportunity.score >= 8 ? theme.colors.success :
    opportunity.score >= 6 ? theme.colors.warning : theme.colors.error

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        padding: theme.spacing[4],
        borderRadius: theme.borderRadius.lg,
        backgroundColor: isHovered ? theme.colors.gray50 : 'transparent',
        cursor: 'pointer',
        transition: theme.transitions.fast,
        border: `1px solid ${isHovered ? theme.colors.border : 'transparent'}`,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing[3], marginBottom: theme.spacing[3] }}>
        <span style={{ fontSize: '32px' }}>{opportunity.flag}</span>
        <div style={{ flex: 1 }}>
          <div style={{
            fontSize: theme.typography.fontSize.base,
            fontWeight: theme.typography.fontWeight.semibold,
            color: theme.colors.textPrimary,
            marginBottom: theme.spacing[1],
          }}>
            {opportunity.country}
          </div>
          <div style={{ display: 'flex', gap: theme.spacing[2], alignItems: 'center' }}>
            <div
              style={{
                fontSize: theme.typography.fontSize.xl,
                fontWeight: theme.typography.fontWeight.bold,
                color: scoreColor,
              }}
            >
              {opportunity.score.toFixed(1)}
            </div>
            <span style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.textSecondary }}>
              / 10
            </span>
          </div>
        </div>
        {isHovered && <ArrowRight size={20} color={theme.colors.primary} />}
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: theme.spacing[2],
        marginBottom: theme.spacing[3],
      }}>
        <div>
          <div style={{
            fontSize: theme.typography.fontSize.xs,
            color: theme.colors.textSecondary,
            marginBottom: theme.spacing[1],
          }}>
            Market Size
          </div>
          <div style={{
            fontSize: theme.typography.fontSize.sm,
            fontWeight: theme.typography.fontWeight.medium,
            color: theme.colors.textPrimary,
          }}>
            {opportunity.marketSize}
          </div>
        </div>
        <div>
          <div style={{
            fontSize: theme.typography.fontSize.xs,
            color: theme.colors.textSecondary,
            marginBottom: theme.spacing[1],
          }}>
            Growth Rate
          </div>
          <div style={{
            fontSize: theme.typography.fontSize.sm,
            fontWeight: theme.typography.fontWeight.medium,
            color: theme.colors.success,
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing[1],
          }}>
            <TrendingUp size={14} />
            +{opportunity.growthRate}%
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: theme.spacing[2] }}>
        <Badge variant={competitionColors[opportunity.competition]} size="sm">
          {opportunity.competition} Competition
        </Badge>
        <Badge variant="neutral" size="sm" icon={<Users size={12} />}>
          {opportunity.sellerCount} sellers
        </Badge>
      </div>
    </div>
  )
}

interface MarketOpportunitiesListProps {
  opportunities?: MarketOpportunity[]
  loading?: boolean
}

export const MarketOpportunitiesList: React.FC<MarketOpportunitiesListProps> = ({
  opportunities,
  loading,
}) => {
  const navigate = useNavigate()

  const defaultOpportunities: MarketOpportunity[] = [
    {
      country: 'Germany',
      countryCode: 'DE',
      flag: '🇩🇪',
      score: 8.7,
      marketSize: '$3.2B',
      growthRate: 23,
      competition: 'Medium',
      sellerCount: 45,
    },
    {
      country: 'France',
      countryCode: 'FR',
      flag: '🇫🇷',
      score: 8.5,
      marketSize: '$2.8B',
      growthRate: 19,
      competition: 'Medium',
      sellerCount: 52,
    },
    {
      country: 'Netherlands',
      countryCode: 'NL',
      flag: '🇳🇱',
      score: 8.2,
      marketSize: '$1.5B',
      growthRate: 28,
      competition: 'Low',
      sellerCount: 23,
    },
    {
      country: 'Spain',
      countryCode: 'ES',
      flag: '🇪🇸',
      score: 7.9,
      marketSize: '$2.1B',
      growthRate: 21,
      competition: 'Medium',
      sellerCount: 38,
    },
    {
      country: 'Italy',
      countryCode: 'IT',
      flag: '🇮🇹',
      score: 7.6,
      marketSize: '$1.9B',
      growthRate: 17,
      competition: 'High',
      sellerCount: 67,
    },
  ]

  const opportunitiesToShow = opportunities || defaultOpportunities

  return (
    <Card>
      <div style={{
        fontSize: theme.typography.fontSize.xl,
        fontWeight: theme.typography.fontWeight.bold,
        color: theme.colors.textPrimary,
        marginBottom: theme.spacing[4],
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing[2],
      }}>
        <MapPin size={24} color={theme.colors.primary} />
        Market Opportunities
      </div>

      {loading ? (
        <div style={{
          padding: theme.spacing[8],
          textAlign: 'center',
          color: theme.colors.textSecondary,
        }}>
          Loading opportunities...
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing[2] }}>
          {opportunitiesToShow.map((opp, index) => (
            <MarketOpportunityItem
              key={index}
              opportunity={opp}
              onClick={() => navigate(`/markets/${opp.countryCode.toLowerCase()}`)}
            />
          ))}
        </div>
      )}

      <div style={{ marginTop: theme.spacing[4], paddingTop: theme.spacing[4], borderTop: `1px solid ${theme.colors.border}` }}>
        <button
          onClick={() => navigate('/markets')}
          style={{
            background: 'none',
            border: 'none',
            color: theme.colors.primary,
            fontSize: theme.typography.fontSize.sm,
            fontWeight: theme.typography.fontWeight.medium,
            cursor: 'pointer',
            padding: 0,
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing[2],
            transition: theme.transitions.fast,
          }}
        >
          View All Markets
          <ArrowRight size={16} />
        </button>
      </div>
    </Card>
  )
}
